import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { DateRange } from '@/components/DateRangeSelector'
import dayjs from 'dayjs'
import { BarChart3 } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { Skeleton } from '@/components/ui/skeleton'
import { getWorkdaysReport } from '../../../services'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
    ReferenceLine,
} from 'recharts'

interface ComplianceAssessmentChartProps {
    dateRange: DateRange
}

interface DepartmentComparison {
    department_id: number
    department_name: string
    current_late: number
    prev_late: number
    diff: number // negative = improvement, positive = worse
}

/**
 * Abbreviate long department names to acronyms
 * e.g., "Phòng Kỹ Thuật" -> "PKT"
 *       "Sales" -> "Sales" (if short enough)
 */
const abbreviateName = (name: string, maxLen = 10): string => {
    if (!name) return '?'

    // If already short enough, return as-is
    if (name.length <= maxLen) {
        return name
    }

    // Create acronym from words (take first letter of each word)
    const words = name.trim().split(/\s+/)
    if (words.length >= 2) {
        const acronym = words.map((w) => w.charAt(0).toUpperCase()).join('')
        return acronym
    }

    // Single long word - truncate
    return name.slice(0, maxLen - 2) + '..'
}

const ComplianceAssessmentChart: React.FC<ComplianceAssessmentChartProps> = ({
    dateRange,
}) => {
    const { t } = useTranslation()

    // Calculate previous month range
    const prevDateRange = useMemo(() => {
        const currentStart = dayjs(dateRange.gte)
        const prevStart = currentStart.subtract(1, 'month').startOf('month')
        const prevEnd = currentStart.subtract(1, 'month').endOf('month')
        return {
            gte: prevStart.format('YYYY-MM-DD'),
            lte: prevEnd.format('YYYY-MM-DD'),
        }
    }, [dateRange])

    // Get month labels
    const currentMonthLabel = useMemo(() => {
        return dayjs(dateRange.gte).format('M')
    }, [dateRange])

    const prevMonthLabel = useMemo(() => {
        return dayjs(prevDateRange.gte).format('M')
    }, [prevDateRange])

    // Fetch current month data
    const { data: currentData, isLoading: isLoadingCurrent } =
        useQueryWithHandler(
            {
                queryKey: [
                    QUERY_KEYS.WORKDAYS_REPORT,
                    'compliance-current',
                    dateRange.gte,
                    dateRange.lte,
                ],
                queryFn: () => getWorkdaysReport(dateRange),
                staleTime: 5 * 60 * 1000,
            },
            { showLoading: false, showError: false }
        )

    // Fetch previous month data
    const { data: prevData, isLoading: isLoadingPrev } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.WORKDAYS_REPORT,
                'compliance-prev',
                prevDateRange.gte,
                prevDateRange.lte,
            ],
            queryFn: () => getWorkdaysReport(prevDateRange),
            staleTime: 5 * 60 * 1000,
        },
        { showLoading: false, showError: false }
    )

    // Calculate comparison data by department
    const comparisonData = useMemo(() => {
        if (!currentData || !prevData) return []

        // Group current data by department
        const currentByDept = currentData.reduce(
            (acc, emp) => {
                const deptId = emp.department_id
                if (!acc[deptId]) {
                    acc[deptId] = {
                        department_id: deptId,
                        department_name: emp.department_name,
                        late_days: 0,
                    }
                }
                acc[deptId].late_days += emp.late_days || 0
                return acc
            },
            {} as Record<number, any>
        )

        // Group previous data by department
        const prevByDept = prevData.reduce(
            (acc, emp) => {
                const deptId = emp.department_id
                if (!acc[deptId]) {
                    acc[deptId] = {
                        department_id: deptId,
                        department_name: emp.department_name,
                        late_days: 0,
                    }
                }
                acc[deptId].late_days += emp.late_days || 0
                return acc
            },
            {} as Record<number, any>
        )

        // Calculate differences
        const allDeptIds = new Set([
            ...Object.keys(currentByDept),
            ...Object.keys(prevByDept),
        ])

        const result: DepartmentComparison[] = []
        allDeptIds.forEach((deptId) => {
            const current = currentByDept[Number(deptId)]
            const prev = prevByDept[Number(deptId)]

            if (current || prev) {
                result.push({
                    department_id: Number(deptId),
                    department_name:
                        current?.department_name ||
                        prev?.department_name ||
                        'Unknown',
                    current_late: current?.late_days || 0,
                    prev_late: prev?.late_days || 0,
                    diff: (current?.late_days || 0) - (prev?.late_days || 0),
                })
            }
        })

        // Sort by diff (most improved first)
        return result.sort((a, b) => a.diff - b.diff)
    }, [currentData, prevData])

    const isLoading = isLoadingCurrent || isLoadingPrev

    // Custom tooltip
    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload?.length) return null
        const data = payload[0].payload as DepartmentComparison

        return (
            <div className="bg-white p-3 border rounded-lg shadow-lg min-w-[180px]">
                <p className="font-semibold text-primary mb-2">
                    Phòng Ban: {data.department_name}
                </p>
                <div className="space-y-1 text-sm">
                    <p className="text-gray-600">
                        Tháng {prevMonthLabel}: đi trễ{' '}
                        <span className="font-medium">
                            {data.prev_late} lần
                        </span>
                    </p>
                    <p className="text-gray-600">
                        Tháng {currentMonthLabel}: đi trễ{' '}
                        <span className="font-medium">
                            {data.current_late} lần
                        </span>
                    </p>
                    <p
                        className={`font-medium ${data.diff < 0 ? 'text-green-600' : data.diff > 0 ? 'text-red-600' : 'text-gray-600'}`}
                    >
                        {data.diff < 0
                            ? `Giảm ${Math.abs(data.diff)} lần`
                            : data.diff > 0
                              ? `Tăng ${data.diff} lần`
                              : 'Không đổi'}
                    </p>
                </div>
            </div>
        )
    }

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg p-4 shadow-md border border-gray-100 h-full">
                <Skeleton className="h-5 w-40 mb-4" />
                <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex items-center gap-2">
                            <Skeleton className="h-4 w-20" />
                            <Skeleton className="h-6 flex-1" />
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <div
            className="bg-white rounded-lg p-4 shadow-md border border-gray-100 h-full"
            data-tour="workday-compliance"
        >
            <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-primary" />
                <Typogaphy className="font-semibold text-primary">
                    {t('workday.compliance_assessment', 'Đánh giá tuân thủ')}
                </Typogaphy>
            </div>

            {comparisonData.length === 0 ? (
                <div className="flex items-center justify-center h-[250px] text-gray-500 text-sm">
                    {t('workday.no_data', 'Không có dữ liệu')}
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                        data={comparisonData}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                    >
                        <XAxis
                            type="number"
                            stroke="#9ca3af"
                            fontSize={11}
                            tickFormatter={(value) =>
                                value > 0 ? `+${value}` : `${value}`
                            }
                        />
                        <YAxis
                            type="category"
                            dataKey="department_name"
                            stroke="#9ca3af"
                            fontSize={11}
                            width={90}
                            tickFormatter={(value) => abbreviateName(value, 10)}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <ReferenceLine x={0} stroke="#9ca3af" strokeWidth={1} />
                        <Bar dataKey="diff" radius={[0, 4, 4, 0]}>
                            {comparisonData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={
                                        entry.diff < 0
                                            ? '#22c55e'
                                            : entry.diff > 0
                                              ? '#fca5a5'
                                              : '#9ca3af'
                                    }
                                    stroke={
                                        entry.diff < 0
                                            ? '#16a34a'
                                            : entry.diff > 0
                                              ? '#ef4444'
                                              : '#6b7280'
                                    }
                                    strokeWidth={1}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-2 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-green-500" />
                    <span>Giảm (cải thiện)</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-red-300" />
                    <span>Tăng (cần cải thiện)</span>
                </div>
            </div>
        </div>
    )
}

export default ComplianceAssessmentChart
